'use client'

import { Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ACCOUNT_CONNECTION_SHEET_CLASS } from '@/features/dashboard/components/account-connection/styles'
import { useLighterIntegratorConsent } from '@/hooks/use-lighter-integrator-consent'
import { formatIntegratorFeePercent } from '@/lib/lighter/integrator-fee'
import { beginManualWalletAction } from '@/lib/lighter/sign-subaccount-key'
import type { LighterPrepareIntegratorConsentResponse } from '@/types/generated/public_api'

// Grants OrderX's Lighter Partner Attribution consent for ONE account.
//
// Opening this sheet IS the approval — it prepares and goes straight to the
// wallet signature rather than parking on an Approve button, since the only
// way to reach it is a control already labelled "Approve OrderX fee".
//
// The rate still renders here, and deliberately so: Lighter's personal_sign
// text is hex throughout (`max perps taker fee: 0x0000000064`), so the wallet
// prompt discloses the fee to nobody. This sheet is the only place the charge
// is stated in readable terms.
type LighterFeeConsentSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: string
  onComplete: () => void | Promise<void>
}

type ConsentPhase = 'preparing' | 'signing' | 'error' | 'awaitingReapproval'

export function LighterFeeConsentSheet({
  open,
  onOpenChange,
  accountId,
  onComplete,
}: LighterFeeConsentSheetProps) {
  const t = useTranslations('accountConnection')
  const { prepare, signAndConfirm } = useLighterIntegratorConsent()
  const [prepared, setPrepared] = useState<LighterPrepareIntegratorConsentResponse | null>(null)
  const [phase, setPhase] = useState<ConsentPhase>('preparing')
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)

  // Held in a ref, not the dep array: both are inline closures from the parent,
  // so depending on them would tear down the effect (cancelling an in-flight
  // signature) on every unrelated parent render.
  const callbacks = useRef({ onComplete, onOpenChange })
  useEffect(() => {
    callbacks.current = { onComplete, onOpenChange }
  })

  useEffect(() => {
    if (!open) return
    let cancelled = false

    const run = async () => {
      setError('')
      setPrepared(null)
      setPhase('preparing')
      try {
        const result = await prepare(accountId)
        if (cancelled) return
        setPrepared(result)
        // An account that already consented gets no surprise wallet prompt —
        // re-approving only refreshes the on-chain expiry, so make it a choice.
        if (result.alreadyApproved) {
          setPhase('awaitingReapproval')
          return
        }
        setPhase('signing')
        await signAndConfirm(result.preparedConsentId, result.personalSignText)
        if (cancelled) return
        await callbacks.current.onComplete()
        callbacks.current.onOpenChange(false)
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : String(e))
        setPhase('error')
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [open, accountId, attempt, prepare, signAndConfirm])

  // Retry (and re-approve) re-prepare from scratch: a failed attempt may have
  // burned the nonce the stashed tx was signed against.
  const handleRetry = () => {
    // Synchronously, before any await: transient user activation does not
    // survive one, and signing with an already-connected wallet never goes
    // through `connectWallet` (which is what would otherwise open this window).
    try {
      beginManualWalletAction()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      return
    }
    setAttempt((n) => n + 1)
  }

  const busy = phase === 'preparing' || phase === 'signing'

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className={ACCOUNT_CONNECTION_SHEET_CLASS}>
        <SheetHeader>
          <SheetTitle>{t('lighterFeeConsentTitle')}</SheetTitle>
        </SheetHeader>
        {error && <p className="text-chart-red text-sm">{error}</p>}
        {phase === 'preparing' ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-white/60">
            <Loader2Icon className="size-4 animate-spin" />
            <span>{t('preparing')}</span>
          </div>
        ) : (
          <div className="flex flex-col gap-4 text-sm">
            {/* `prepared` is null when prepare itself failed — the retry below
                still has to render, or that error is a dead end. */}
            {prepared && (
              <>
                <p className="text-white/70">
                  {prepared.alreadyApproved
                    ? t('lighterFeeConsentAlreadyApproved')
                    : t('lighterFeeConsentDescription')}
                </p>
                <dl className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <dt className="text-white/60">{t('lighterFeeConsentRate')}</dt>
                    <dd className="font-medium">
                      {formatIntegratorFeePercent(prepared.maxFeeUnits)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-white/60">{t('lighterFeeConsentIntegrator')}</dt>
                    <dd className="font-mono text-xs">{prepared.integratorAccountIndex}</dd>
                  </div>
                </dl>
                <p className="text-xs text-white/50">{t('lighterFeeConsentRevoke')}</p>
              </>
            )}
            {phase === 'signing' ? (
              <p className="flex items-center gap-2 text-white/70">
                <Loader2Icon className="size-4 animate-spin" />
                {t('lighterFeeConsentSigning')}
              </p>
            ) : (
              <Button onClick={handleRetry} disabled={busy}>
                {prepared?.alreadyApproved
                  ? t('lighterFeeConsentReapprove')
                  : t('lighterFeeConsentRetry')}
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
